import hcBase from "../common/HighchartsBase"
import FilterFactory from "../common/FilterFactory"
import "../css/trend.css"
import styles from "styles-to-js-loader/styles"
import colors from "../common/color"
import { fetchTrendData, formatData } from "../common/FetchFilterData"

const filterUrl =
    "https://app.vantetider.se/spa/1.1/rest/filters/filterchoices/availabilityIncomingfilter"
const reqUrl =
    "https://app.vantetider.se/spa/1.1/rest/availableHealthcareIncomingStatistics/production"

// Filter configuration: specifies value and label keys and API filter key
const FILTER_CONFIG = {
    regions: {
        valueKey: "value",
        nameKey: "name",
        apiFilterKey: "regionId"
    },
    phase: {
        valueKey: "value",
        nameKey: "phase",
        apiFilterKey: "phase"
    },
    years: {
        valueKey: "value",
        nameKey: "name",
        apiFilterKey: "yearId"
    }
}

const SERIES_COLOR_PALETTE = colors.allColors
const defaultChartOptions = {
    chart: {
        height: (9 / 16) * 100 + "%",
        width: null
    },
    title: {
        text: null,
        style: {
            display: "none"
        }
    },
    data: {
        dateFormat: "YYYY/mm"
    },
    tooltip: {
        shared: false,
        valueSuffix: "{series.options.unit}"
    },
    loading: {
        hideDuration: 500,
        showDuration: 1000
    },
    xAxis: {
        type: "category",
        categories: [],
        title: {
            text: null
        },
        min: 0,
        labels: {
            rotation: -45
        }
    },
    yAxis: [
        {
            //Primary axis, count
            min: 0,
            tickAmount: 9,
            title: {
                text: "Antal",
                align: "middle",
                offset: undefined,
                rotation: 270,
                x: 0,
                y: undefined
            },
            labels: {
                formatter: function () {
                    return this.value
                        ? this.value
                              .toString()
                              .replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                        : null
                },
                overflow: "justify"
            }
        },
        {
            //Secondary axis
            min: 0,
            max: 100,
            tickAmount: 3,
            title: {
                text: "Andel",
                align: "middle",
                offset: undefined,
                rotation: 270,
                x: 0,
                y: undefined
            },
            labels: {
                format: "{value}%"
            },
            opposite: true
        }
    ],
    legend: {
        floating: false,
        borderWidth: 1,
        shadow: true,
        useHTML: false
    },
    credits: {
        enabled: false
    },
    plotOptions: {
        column: {
            stacking: "normal"
            //pointWidth: 13,
        }
    },
    lang: {
        loading: "",
        noData: "Ingen data för valt urval",
        accessibility: {
            chartTypes: {
                defaultMultiple:
                    "Linjediagram och stapeldigram med {numSeries} antal serier."
            },
            axis: {
                xAxisDescriptionSingular:
                    "Diagrammet har 1 X-axel som visar tid i månader",
                yAxisDescriptionPlural:
                    "Diagrammet har 2 Y-axlar. En relaterad till staplarna som visar antal. Den andra är relaterad till linje grafen, som visar andel, 0 till 100 procent."
            },
            screenReaderSection: {
                beforeRegionLabel: "Början av diagrammet",
                afterRegionLabel: "Slutet av diagrammet"
            }
        }
    },
    accessibility: {
        description:
            "Diagram som visar hur utvecklingen sett ut i specialvården under det angivna tidsspannet för olika genomförda besökstyper relaterat kring covid."
    },
    exporting: {
        showTable: false
    },
    responsive: {
        rules: [
            {
                //If chart is to small (mobile), apply these options instead
                condition: {
                    maxWidth: 600
                },
                chartOptions: {
                    chart: {
                        height: 450
                    },
                    plotOptions: {
                        column: {
                            stacking: "normal"
                            //pointWidth: 6.5
                        }
                    },
                    xAxis: {
                        labels: {
                            formatter: function () {
                                if (this.value) {
                                    return this.value.substr(0, 3)
                                } else {
                                    return null
                                }
                            }
                        }
                    },
                    yAxis: [
                        {
                            //Primary axis, count
                            min: 0,
                            tickAmount: 3,
                            title: {
                                text: "Antal",
                                align: "high",
                                offset: 0,
                                rotation: 0,
                                x: 25,
                                y: -1
                            },
                            labels: {
                                formatter: function () {
                                    const num = this.value
                                    if (!num) return null
                                    return Math.abs(num) > 999
                                        ? `${((Math.sign(num) * Math.abs(num)) / 1000).toFixed(1)}k`
                                        : Math.sign(num) * Math.abs(num)
                                },
                                overflow: "justify"
                            }
                        },
                        {
                            //Secondary axis
                            min: 0,
                            max: 100,
                            tickAmount: 3,
                            title: {
                                align: "high",
                                offset: 0,
                                rotation: 0,
                                x: -25,
                                y: -1
                            },
                            labels: {
                                format: "{value}%"
                            },
                            opposite: true
                        }
                    ]
                }
            }
        ]
    }
}
class WaitOverviewTrend extends hcBase {
    constructor() {
        const template = document.createElement("form")
        super(template)

        this.getShadow().appendChild(styles.get())
        this.chartDiv = document.createElement("div")
        this.filterDiv = document.createElement("div")
        template.appendChild(this.filterDiv)
        template.appendChild(this.chartDiv)
        this.showEmptyChart(this.chartDiv, defaultChartOptions)

        this.filters = {}
        this.loadData()
    }

    async loadData() {
        try {
            const res = await formatData({}, filterUrl)

            // Dynamically create filters from API response and config
            Object.entries(FILTER_CONFIG).forEach(
                ([filterKey, { valueKey, nameKey, apiFilterKey }]) => {
                    if (!res[filterKey] || !Array.isArray(res[filterKey]))
                        return

                    if (filterKey === "regions") {
                        res[filterKey].unshift({
                            [nameKey]: "Alla regioner",
                            [valueKey]: -1
                        })
                    }

                    const filterData = res[filterKey].map((item) => ({
                        name: item[nameKey],
                        value: item[valueKey]
                    }))

                    const filterElement = new FilterFactory("select", {}, {})
                    new FilterFactory("options", filterData, {}).forEach((o) =>
                        filterElement.appendChild(o)
                    )

                    this.filters[filterKey] = {
                        element: filterElement,
                        dataKey: valueKey,
                        apiFilterKey
                    }

                    this.filterDiv.appendChild(filterElement)

                    // Add change listener
                    filterElement.addEventListener("change", () =>
                        this.update()
                    )
                }
            )

            this.allData = res
            this.update()
        } catch (err) {
            console.error(err)
            this.errorNoData()
        }
    }

    //Gets data and updates the graph
    async update() {
        if (this.chart) this.chart.showLoading()

        try {
            const res = await fetchTrendData(reqUrl, this.getValues(), "series")
            const opacityTrackers = {}
            const OPACITY_START = 0.75
            const OPACITY_STEP = 0.025
            const nameOrder = [
                ...(res.serieNames || []),
                ...(res.seriePartNames || [])
            ]
            const nameColorMap = new Map()
            const getColorForName = (name) => {
                if (!name) return SERIES_COLOR_PALETTE[0]
                if (!nameColorMap.has(name)) {
                    const index = nameColorMap.size
                    nameColorMap.set(
                        name,
                        SERIES_COLOR_PALETTE[
                            index % SERIES_COLOR_PALETTE.length
                        ]
                    )
                }
                return nameColorMap.get(name)
            }

            res.series.forEach((s) => {
                const isColumn =
                    s.stack || (res.serieNames || []).includes(s.name)
                const seriesKey = s.stack || s.name
                const isPercent = (res.seriePartNames || []).includes(s.name)

                Object.assign(s, {
                    linkedTo: seriesKey,
                    showInLegend: true,
                    type: isColumn ? "column" : "spline",
                    yAxis: isColumn ? 0 : 1,
                    color: getColorForName(seriesKey),
                    unit: isPercent ? "%" : ""
                })

                if (isColumn) {
                    const group = seriesKey
                    if (!opacityTrackers[group]) {
                        opacityTrackers[group] = OPACITY_START
                    }
                    s.opacity = opacityTrackers[group]
                    opacityTrackers[group] += OPACITY_STEP
                }
            })

            this.series = res.series
            this.categories = res.xNames
            this.setChartOptions(defaultChartOptions)
            this.render(this.chartDiv)
            if (this.chart) this.chart.hideLoading()
        } catch (err) {
            console.error(err)
            this.errorNoData()
        }
    }

    getValues() {
        const values = {}

        // Dynamically build values object from all filters
        Object.entries(this.filters).forEach(([key, filterConfig]) => {
            const value = filterConfig.element.value

            console.log(`getvalues: ${key}`, value)
            if (value !== "-1") {
                values[filterConfig.apiFilterKey] = value
            }
        })

        return values
    }
}
if (!customElements.get("wait-overview-trend")) {
    hcBase.define("wait-overview-trend", WaitOverviewTrend)
}
